import classNames from 'classnames';
import { ErrorBoundary } from 'react-error-boundary';
import { Container, Heading } from 'yoso';
import { QueryErrorBoundary } from '~/components/Error';
import { T } from '~/components/I18n';
import { LevelStatusCardList } from '~/components/Level/Status/LevelStatusCardList';
import { LevelUpGuide } from '~/components/LevelUp/Guide/LevelUpGuide';
import { LevelUpHero } from '~/components/LevelUp/Hero/LevelUpHero';
import { HowToCollect } from '~/components/LevelUp/HowTo/Collect/HowToCollect';
import { HowToEarn } from '~/components/LevelUp/HowTo/Earn/HowToEarn';
import { HowToLevelUp } from '~/components/LevelUp/HowTo/LevelUp/HowToLevelUp';
import { LevelUpStatusBar } from '~/components/LevelUp/StatusBar/LevelUpStatusBar';
import { useMember } from '~/components/Member/Member.hooks';
import { Page } from '~/components/Page';
import { Section } from '~/components/Page/Section/Section';
import { HashScroll } from '~/components/Route/Scroll/HashScroll';
import { StatusBarErrorFallback } from '~/components/Status/Bar/ErrorFallback/StatusBarErrorFallback';
import { UnauthorizedStatusBar } from '~/components/Status/Bar/Unauthorized/UnauthorizedStatusBar';
import styles from './LevelUpPage.module.scss';

export type LevelUpPageProps = {
  className?: string;
};

export const LevelUpPage = ({ className }: LevelUpPageProps) => {
  const {
    data: { isLoggedIn },
  } = useMember();
  return (
    <Page className={classNames(styles.root, className)} data-event-page_name="level_up_main">
      <Container margin="auto">
        <Section className={styles.pageSection}>
          <Section className={styles.heroSection}>
            <LevelUpHero />
            <ErrorBoundary fallbackRender={(props) => <StatusBarErrorFallback {...props} />}>
              {isLoggedIn ? (
                <LevelUpStatusBar className={styles.statusBar} />
              ) : (
                <UnauthorizedStatusBar className={styles.statusBar} />
              )}
            </ErrorBoundary>
          </Section>
          <Section>
            <HowToLevelUp />
          </Section>
          <HashScroll hash="#levelUp" behavior="smooth" position="start">
            <Section>
              <QueryErrorBoundary>
                <Heading level={3} className={styles.levelUpTitle}>
                  <T name="citizen_levelup_status_title" />
                </Heading>
                <LevelStatusCardList />
              </QueryErrorBoundary>
            </Section>
          </HashScroll>
          <HashScroll hash="#levelUpGuide" behavior="smooth" position="start">
            <Section className={styles.guideSection}>
              <LevelUpGuide />
            </Section>
          </HashScroll>
          <Section>
            <HowToCollect />
          </Section>
          <Section>
            <HowToEarn />
          </Section>
        </Section>
      </Container>
    </Page>
  );
};
